import { values, indexBy, prop, sortBy } from 'ramda';
import { createSelector } from 'reselect';
import moment from 'moment-timezone';

import { getReactors } from '../api';
import logger from '../utils/logger';

import { productionSelector } from './productions';
import { reactorUnavalabilitiesSelector } from './unavailabilities';

export const REACTORS_LOAD_ACTION = 'REACTORS_LOAD_ACTION';
export const REACTORS_RECEIVE_ACTION = 'REACTORS_RECEIVE_ACTION';

function loadReactorsAction() {
  return { type: REACTORS_LOAD_ACTION };
}

function receiveReactorsAction({ data, errors }) {
  return { type: REACTORS_RECEIVE_ACTION, data, errors };
}

export function loadAllReactors() {
  return function dispatchLoadAllReactors(dispatch) {
    dispatch(loadReactorsAction());
    return getReactors()
      .then(data =>
        dispatch(
          receiveReactorsAction({
            data: indexBy(prop('eicCode'), data),
          }),
        ),
      )
      .catch(
        errors =>
          logger.error('Failed to load reactors:', errors) ||
          dispatch(receiveReactorsAction({ errors })),
      );
  };
}

const initialState = {
  loading: false,
  loaded: false,
  errors: null,
  data: {},
};

function reactorsReducer(state = initialState, action) {
  const { type } = action;
  switch (type) {
    case REACTORS_LOAD_ACTION:
      return { ...state, loading: true };
    case REACTORS_RECEIVE_ACTION:
      return {
        ...state,
        loading: false,
        loaded: !action.errors,
        errors: action.errors || null,
        data: action.data || {},
      };
    default:
      return state;
  }
}

export function reactorsLoadedSelector(state) {
  return state.reactors?.loaded || false;
}

function getStatus(unavailabilities, inputDate) {
  if (!Array.isArray(unavailabilities) || unavailabilities.length === 0) {
    return 'RUNNING';
  }

  const date = moment(inputDate);
  const currentUnavailability = unavailabilities.find(u =>
    date.isBetween(u.startDate, u.endDate, undefined, '[)'),
  );

  if (currentUnavailability) {
    if (currentUnavailability.type === 'PLANNED_MAINTENANCE') {
      if (currentUnavailability.availablePower_MW === 0) {
        return 'PLANNED_STOP';
      }
      return 'PLANNED_REDUCTION';
    }
    if (currentUnavailability.availablePower_MW === 0) {
      return 'AUTO_STOP';
    }
    return 'UNPLANNED_REDUCTION';
  }
  return 'RUNNING';
}

export function reactorSelector({ eicCode, date }, state) {
  const reactor = state.reactors?.data?.[eicCode];
  if (!reactor) {
    return null;
  }

  const dayProductions = productionSelector({ eicCode, date }, state);
  const unavailabilities = reactorUnavalabilitiesSelector(
    { eicCode, date },
    state,
  );

  return {
    ...reactor,
    status: getStatus(unavailabilities || [], date),
    dayProductions: dayProductions || [],
    unavailabilities: unavailabilities || [],
  };
}

export const reactorsSelector = createSelector(
  [
    state => state.reactors?.data || {},
    (state, { date }) => date,
    (state, { date }) =>
      state.productions?.[moment(date).format('YYYY-MM-DD')] || null,
    (state, { date }) =>
      state.unavailabilities?.[moment(date).format('YYYY-MM-DD')] || null,
  ],
  (reactorsData, date, productionsForDate, unavailabilitiesForDate) => {
    return values(reactorsData)
      .map(reactor => {
        // Get production data
        const productionData = productionsForDate?.data?.[reactor.eicCode];
        const dayProductions = productionData?.values
          ? productionData.values.map(d => ({
              value: Math.max(d.value || 0, 0),
            }))
          : [];

        // Get unavailability data using the proper selector
        const unavailabilityData =
          reactorUnavalabilitiesSelector(
            { eicCode: reactor.eicCode, date },
            {
              unavailabilities: {
                [moment(date).format('YYYY-MM-DD')]: unavailabilitiesForDate,
              },
            },
          ) || [];

        return {
          ...reactor,
          status: getStatus(unavailabilityData, date),
          dayProductions,
          unavailabilities: unavailabilityData,
        };
      })
      .filter(reactor => reactor !== null);
  },
);

// Memoized selector for reactors of a specific plant
export const reactorsOfPlantSelector = createSelector(
  [
    state => state.reactors?.data || {},
    (state, { plantId }) => plantId,
    (state, { date }) => date,
    (state, { date }) =>
      state.productions?.[moment(date).format('YYYY-MM-DD')] || null,
    (state, { date }) =>
      state.unavailabilities?.[moment(date).format('YYYY-MM-DD')] || null,
  ],
  (
    reactorsData,
    plantId,
    date,
    productionsForDate,
    unavailabilitiesForDate,
  ) => {
    return Object.values(reactorsData)
      .filter(reactor => reactor.plantId === plantId)
      .map(reactor => {
        // Get production data
        const productionData = productionsForDate?.data?.[reactor.eicCode];
        const dayProductions = productionData?.values
          ? productionData.values.map(d => ({
              value: Math.max(d.value || 0, 0),
            }))
          : [];

        // Get unavailability data
        const unavailabilityData =
          unavailabilitiesForDate?.data?.[reactor.eicCode] || [];

        return {
          ...reactor,
          status: getStatus(unavailabilityData, date),
          dayProductions,
          unavailabilities: unavailabilityData,
        };
      })
      .sort((a, b) => a.reactorIndex - b.reactorIndex);
  },
);

const INIT_DATA = {
  availablePower: 0,
  totalPower: 0,
  availableCount: 0,
  partiallyUnavailableCount: 0,
  totallyUnavailableCount: 0,
};
export function reactorSetIndicatorsSelector(
  { date: dateString, slotIndex },
  state,
) {
  // Anchor everything in Paris local time to avoid UTC day rollovers
  const parisDay = moment.tz(dateString, 'Europe/Paris').startOf('day');
  const date = parisDay.clone().add((slotIndex ?? 0) * 15, 'minutes');
  // Important: pull unavailabilities for the same Paris-local day
  const reactors = reactorsSelector(state, { date: parisDay });

  const result = reactors.reduce((res, reactor) => {
    const res2 = {
      availablePower: 0,
      availableCount: 0,
      partially: 0,
      totally: 0,
    };

    const currentUnavailability = reactor.unavailabilities.find(u =>
      date.isBetween(u.startDate, u.endDate, undefined, '[)'),
    );

    if (currentUnavailability) {
      if (currentUnavailability.availablePower_MW === 0) {
        res2.totally = 1;
      } else {
        res2.availableCount = 1;
        res2.partially = 1;
      }
      res2.availablePower = currentUnavailability.availablePower_MW;
    } else {
      res2.availableCount = 1;
      res2.availablePower = reactor.netPower_MW;
    }

    return {
      totalPower: res.totalPower + reactor.netPower_MW,
      availablePower: res.availablePower + res2.availablePower,
      availableCount: res.availableCount + res2.availableCount,
      totallyUnavailableCount: res.totallyUnavailableCount + res2.totally,
      partiallyUnavailableCount: res.partiallyUnavailableCount + res2.partially,
    };
  }, INIT_DATA);

  return result;
}

export function reactorByPlantAndIndexSelector(
  { plantId, reactorIndex, date },
  state,
) {
  if (!state.reactors?.data) {
    return null;
  }
  const reacto = Object.values(state.reactors.data).find(
    reactor =>
      reactor.plantId === plantId && reactor.reactorIndex === reactorIndex,
  );
  return reacto
    ? reactorSelector({ eicCode: reacto.eicCode, date }, state)
    : null;
}

export function reactorsOfPlant(plantId, state) {
  return Object.values(state.reactors.data)
    .filter(r => r.plantId === plantId)
    .map(r => reactorSelector(r.eicCode, state));
}

export default reactorsReducer;
